import { useEffect, useRef } from "react";
import { isPrimaryModifierLetter, shouldIgnoreKeyboardShortcut } from "@/lib/keyboardShortcut";

/**
 * Opens the create flow owned by the currently mounted page with Ctrl+K on
 * Windows or Command+K on macOS.
 *
 * `preventDefault` suppresses browser search when the page receives the event.
 * Browsers do not expose key events to the app while focus is in their own
 * address/search UI, so the shortcut cannot be reclaimed from browser chrome.
 */
export function useCreateShortcut(onCreate: () => void, enabled = true) {
  const onCreateRef = useRef(onCreate);
  onCreateRef.current = onCreate;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.repeat ||
        event.isComposing ||
        shouldIgnoreKeyboardShortcut(event) ||
        !isPrimaryModifierLetter(event, "k")
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onCreateRef.current?.();
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [enabled]);
}
